import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import type { ReportName } from '@wrapforce/shared';
import { PLAN_DEFINITIONS, type Plan } from '@wrapforce/shared/constants';
import { Lock, RefreshCw } from 'lucide-react';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChartTooltip } from '@/components/charts/ChartTooltip';
import { cn } from '@/lib/cn';
import { useLocaleFormatters } from '@/lib/format';
import { useLocationStore, ALL_LOCATIONS } from '@/stores/location';
import {
  exportReportExcel, useReportCredits, useReportEmployees, useReportKpis, useReportLeadFunnel, useReportRevenueByService, useReportRevenueMonthly,
  type ReportRange,
} from './api';

type Preset = 'mtd' | 'last30' | 'qtd' | 'ytd' | 'custom';

function rangeForPreset(preset: Exclude<Preset, 'custom'>): ReportRange {
  const now = new Date();
  const iso = (d: Date) => d.toISOString();
  const startOfDay = (d: Date) => {
    d.setHours(0, 0, 0, 0);
    return d;
  };
  switch (preset) {
    case 'mtd':
      return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)) };
    case 'last30':
      return { from: iso(startOfDay(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000))) };
    case 'qtd':
      return { from: iso(new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)) };
    case 'ytd':
      return { from: iso(new Date(now.getFullYear(), 0, 1)) };
  }
}

export function ReportsPage() {
  const { t, i18n } = useTranslation('reports');
  const { formatMoney, formatRelative } = useLocaleFormatters();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const plan = (membership?.plan ?? 'trial') as Plan;
  const canExport = PLAN_DEFINITIONS[plan].gates.csvExport;
  const activeLocationId = useLocationStore((s) => s.activeLocationId);
  const gateOn = membership ? PLAN_DEFINITIONS[membership.plan].gates.multiLocation : false;
  const locationId = gateOn && activeLocationId !== ALL_LOCATIONS ? activeLocationId : undefined;

  const [preset, setPreset] = useState<Preset>('last30');
  const [custom, setCustom] = useState<ReportRange>({});
  const range = useMemo(() => (preset === 'custom' ? custom : rangeForPreset(preset)), [preset, custom]);

  const queryClient = useQueryClient();
  const isFetching = useIsFetching({ queryKey: [workspaceId, 'reports'] }) > 0;
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  function handleRefresh() {
    void queryClient.invalidateQueries({ queryKey: [workspaceId, 'reports'] });
  }

  const kpisQuery = useReportKpis(workspaceId, range, locationId);
  const kpis = kpisQuery.data;
  const { data: revenueMonthly = [] } = useReportRevenueMonthly(workspaceId, range, locationId);
  const { data: revenueByService = [] } = useReportRevenueByService(workspaceId, range, locationId);
  const { data: leadFunnel = [] } = useReportLeadFunnel(workspaceId, range, locationId);
  const { data: employees = [] } = useReportEmployees(workspaceId, range, locationId);
  const { data: creditUsage = [] } = useReportCredits(workspaceId, range, locationId);

  const totalCreditsSpent = useMemo(() => creditUsage.reduce((s, d) => s + d.creditsSpent, 0), [creditUsage]);

  async function handleExport(report: ReportName) {
    if (!canExport || !workspaceId) return;
    try {
      await exportReportExcel(workspaceId, report, range, i18n.language, locationId);
    } catch {
      toast.error(t('toast.exportFailed'));
    }
  }

  const PRESET_LABEL_KEYS: Record<Preset, string> = {
    mtd: 'preset.mtd',
    last30: 'preset.last30',
    qtd: 'preset.qtd',
    ytd: 'preset.ytd',
    custom: 'preset.custom',
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 overflow-x-auto">
          {(['mtd', 'last30', 'qtd', 'ytd', 'custom'] as Preset[]).map((p) => (
            <button
              key={p}
              onClick={() => setPreset(p)}
              className={cn('whitespace-nowrap px-3 py-1.5 text-xs font-semibold', preset === p ? 'bg-ink text-surface' : 'text-muted hover:bg-surface-2')}
            >
              {t(PRESET_LABEL_KEYS[p])}
            </button>
          ))}
          {preset === 'custom' ? (
            <div className="ml-2 flex items-center gap-1.5 text-xs text-muted">
              <input type="date" onChange={(e) => setCustom((c) => ({ ...c, from: new Date(e.target.value).toISOString() }))} className="border border-border bg-surface px-2 py-1" />
              <span>{t('customRangeTo')}</span>
              <input type="date" onChange={(e) => setCustom((c) => ({ ...c, to: new Date(e.target.value).toISOString() }))} className="border border-border bg-surface px-2 py-1" />
            </div>
          ) : null}
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs text-muted">
          {kpisQuery.dataUpdatedAt ? <span>{t('updated', { time: formatRelative(kpisQuery.dataUpdatedAt) })}</span> : null}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isFetching}
            className="flex items-center gap-1.5 border border-border px-2 py-1 font-semibold text-ink hover:bg-surface-2 disabled:opacity-50"
          >
            <RefreshCw size={12} className={cn(isFetching && 'animate-spin')} />
            {t('refresh')}
          </button>
        </div>
      </div>

      {kpis ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kpi label={t('kpi.revenue')} value={formatMoney(kpis.revenueCents)} />
          <Kpi label={t('kpi.depositsCollected')} value={formatMoney(kpis.depositsCents)} />
          <Kpi label={t('kpi.openQuotes')} value={String(kpis.openQuotesCount)} sub={formatMoney(kpis.openQuotesValueCents)} />
          <Kpi label={t('kpi.jobsCompleted')} value={String(kpis.jobsCompleted)} sub={t('kpi.avgPrefix', { value: formatMoney(kpis.avgJobValueCents) })} />
        </div>
      ) : null}

      <ReportSection title={t('section.revenueByMonth')} canExport={canExport} onExport={() => handleExport('revenue-monthly')}>
        <div className="h-56 p-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revenueMonthly} barCategoryGap="28%">
              <defs>
                <linearGradient id="revenueMonthlyBarFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-accent)" stopOpacity={1} />
                  <stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0.55} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="var(--color-border)" strokeDasharray="3 4" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-muted)' }} axisLine={false} tickLine={false} />
              <YAxis hide domain={[0, 'dataMax']} />
              <Tooltip cursor={{ fill: 'var(--color-surface-2)', opacity: 0.6 }} content={<ChartTooltip valueFormatter={(v) => formatMoney(v)} />} />
              <Bar dataKey="revenueCents" fill="url(#revenueMonthlyBarFill)" radius={0} activeBar={{ fill: 'var(--color-accent-hover)' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ReportSection>

      <ReportSection title={t('section.revenueByService')} canExport={canExport} onExport={() => handleExport('revenue-by-service')}>
        <div className="h-56 p-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revenueByService} layout="vertical" barCategoryGap="28%">
              <defs>
                <linearGradient id="revenueByServiceBarFill" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="var(--color-info)" stopOpacity={1} />
                  <stop offset="100%" stopColor="var(--color-info)" stopOpacity={0.55} />
                </linearGradient>
              </defs>
              <CartesianGrid horizontal={false} stroke="var(--color-border)" strokeDasharray="3 4" />
              <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--color-muted)' }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="serviceName" width={110} tick={{ fontSize: 11, fill: 'var(--color-muted)' }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: 'var(--color-surface-2)', opacity: 0.6 }} content={<ChartTooltip valueFormatter={(v) => formatMoney(v)} />} />
              <Bar dataKey="revenueCents" fill="url(#revenueByServiceBarFill)" radius={0} activeBar={{ fill: 'var(--color-accent-hover)' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ReportSection>

      <ReportSection title={t('section.leadFunnel')} canExport={canExport} onExport={() => handleExport('lead-funnel')}>
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-surface-2 text-left text-xs font-bold uppercase tracking-wide text-muted">
            <tr>
              <th className="px-4 py-2">{t('table.source')}</th>
              <th className="px-4 py-2">{t('table.new')}</th>
              <th className="px-4 py-2">{t('table.contacted')}</th>
              <th className="px-4 py-2">{t('table.qualified')}</th>
              <th className="px-4 py-2">{t('table.quoted')}</th>
              <th className="px-4 py-2">{t('table.won')}</th>
              <th className="px-4 py-2">{t('table.conversion')}</th>
            </tr>
          </thead>
          <tbody>
            {leadFunnel.map((row) => (
              <tr key={row.source} className="border-b border-border last:border-b-0">
                <td className="px-4 py-2 font-medium capitalize text-ink">{row.source.replace('_', ' ')}</td>
                <td className="px-4 py-2 font-mono">{row.total}</td>
                <td className="px-4 py-2 font-mono">{row.contacted}</td>
                <td className="px-4 py-2 font-mono">{row.qualified}</td>
                <td className="px-4 py-2 font-mono">{row.quoted}</td>
                <td className="px-4 py-2 font-mono">{row.won}</td>
                <td className="px-4 py-2 font-mono">{row.total > 0 ? `${Math.round((row.won / row.total) * 100)}%` : '-'}</td>
              </tr>
            ))}
            {leadFunnel.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-muted">{t('empty.noLeads')}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </ReportSection>

      <ReportSection title={t('section.employeePerformance')} canExport={canExport} onExport={() => handleExport('employees')}>
        <EmployeeTable rows={employees} />
      </ReportSection>

      <ReportSection title={t('section.aiCreditUsage')} canExport={canExport} onExport={() => handleExport('credits')}>
        <div className="flex items-center justify-between px-4 pt-3">
          <span className="text-xs text-muted">{t('totalSpent')}</span>
          <span className="font-mono text-lg font-bold">{totalCreditsSpent}</span>
        </div>
        <div className="h-48 p-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={creditUsage}>
              <CartesianGrid vertical={false} stroke="var(--color-border)" strokeDasharray="3 4" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'var(--color-muted)' }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: 'var(--color-surface-2)', opacity: 0.6 }} content={<ChartTooltip valueFormatter={(v) => String(v)} />} />
              <Area type="monotone" dataKey="creditsSpent" stroke="var(--color-accent)" fill="var(--color-accent)" fillOpacity={0.2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </ReportSection>

      <p className="text-center text-xs text-muted">{t('exportsNote')}</p>
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1.5 p-4">
        <span className="text-[10px] font-semibold uppercase tracking-[.12em] text-muted">{label}</span>
        <span className="font-mono text-[26px] font-semibold text-ink">{value}</span>
        {sub ? <span className="text-[11.5px] text-muted">{sub}</span> : null}
      </CardContent>
    </Card>
  );
}

function ReportSection({ title, canExport, onExport, children }: { title: string; canExport: boolean; onExport: () => void; children: React.ReactNode }) {
  const { t } = useTranslation('reports');
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {canExport ? (
          <Button size="sm" variant="outline" className="ml-auto" onClick={onExport}>
            {t('exportExcel')}
          </Button>
        ) : (
          <span className="ml-auto flex items-center gap-1.5 text-xs text-muted" title={t('upgradeToExport')}>
            <Lock size={12} /> {t('exportExcel')}
          </span>
        )}
      </CardHeader>
      <CardContent className="p-0">{children}</CardContent>
    </Card>
  );
}

function EmployeeTable({ rows }: { rows: import('@wrapforce/shared').EmployeePerformancePoint[] }) {
  const { t } = useTranslation('reports');
  const { formatMoney } = useLocaleFormatters();
  const [sortKey, setSortKey] = useState<'revenueCents' | 'quotesCreated' | 'jobsCompleted'>('revenueCents');
  const sorted = [...rows].sort((a, b) => b[sortKey] - a[sortKey]);

  return (
    <table className="w-full text-sm">
      <thead className="border-b border-border bg-surface-2 text-left text-xs font-bold uppercase tracking-wide text-muted">
        <tr>
          <th className="px-4 py-2">{t('employeeTable.member')}</th>
          <th className="cursor-pointer px-4 py-2" onClick={() => setSortKey('quotesCreated')}>{t('employeeTable.quotesCreated')}</th>
          <th className="px-4 py-2">{t('employeeTable.quotesSent')}</th>
          <th className="px-4 py-2">{t('employeeTable.quotesWon')}</th>
          <th className="cursor-pointer px-4 py-2" onClick={() => setSortKey('revenueCents')}>{t('employeeTable.revenue')}</th>
          <th className="cursor-pointer px-4 py-2" onClick={() => setSortKey('jobsCompleted')}>{t('employeeTable.jobsCompleted')}</th>
          <th className="px-4 py-2">{t('employeeTable.avgTurnaround')}</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((row) => (
          <tr key={row.userId} className="border-b border-border last:border-b-0">
            <td className="px-4 py-2 font-medium text-ink">{row.fullName || '-'}</td>
            <td className="px-4 py-2 font-mono">{row.quotesCreated}</td>
            <td className="px-4 py-2 font-mono">{row.quotesSent}</td>
            <td className="px-4 py-2 font-mono">{row.quotesWon}</td>
            <td className="px-4 py-2 font-mono">{formatMoney(row.revenueCents)}</td>
            <td className="px-4 py-2 font-mono">{row.jobsCompleted}</td>
            <td className="px-4 py-2 font-mono">{row.avgTurnaroundHours !== null ? `${row.avgTurnaroundHours.toFixed(1)}h` : '-'}</td>
          </tr>
        ))}
        {sorted.length === 0 ? (
          <tr>
            <td colSpan={7} className="px-4 py-6 text-center text-muted">{t('employeeTable.noActivity')}</td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}
